import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal"
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { useEffect, useMemo } from "react";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { InspecaoObraExecutadoPorForm, InspecaoObraExecutadoPorSchema } from "../../../schemas/ObraItensChecklistArea_verificacao/inspecao_obra_executado_por";
import { zodResolver } from "@hookform/resolvers/zod";
import { FornecedorDropDown } from "../../../../../../common/domain/models/fornecedor_dropdown";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { FuncionarioTercDropDown } from "../../../../../../common/domain/models/funcionario_terc_dropdown";

import FormContainer from "../../../../../../common/components/form_container";

/**
 * ExecutadoPorModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  fornecedoresDropDown: FornecedorDropDown[];
  funcionariosDropDown: FuncionarioDropDown[];
  funcionariosTercDropDown: FuncionarioTercDropDown[];
  onClose: () => void;
  onSubmit: (form: InspecaoObraExecutadoPorForm) => void;
};

export const ExecutadoPorModal = ({
  open,
  saving,
  loading,
  fornecedoresDropDown,
  funcionariosDropDown,
  funcionariosTercDropDown,
  onClose,
  onSubmit,
}: Props) => {
  const hookForm = useForm<InspecaoObraExecutadoPorForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { realizadoPor: 0 },
    resolver: zodResolver(InspecaoObraExecutadoPorSchema),
  });

  const [realizadoPor, idFornecedor] = hookForm.watch(['realizadoPor', 'idFornecedor']);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Funcionário Terceirizado',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Funcionário Próprio',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      hookForm.reset();
    }
  }, [
    open,
    hookForm,
  ]);

  useEffect(() => {
    hookForm.setValue('idFornecedor', null);
    hookForm.setValue('funcionarios', null);
  }, [
    realizadoPor,
    hookForm,
  ]);

  useEffect(() => {
    hookForm.setValue('funcionarios', null);
  }, [
    idFornecedor,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      width="40rem"
      title='Executado Por'
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title="Informações" arrangement="fullRow" border={false} padding="small">
        <>
          <FormRadioGroupInput
            name='realizadoPor'
            label='Serviço Realizado Por'
            options={statusOptions}
            reactHookForm={hookForm}
          />
          {
            Number(realizadoPor) === 0 &&
            <DefaultFormAutocomplete
              name='idFornecedor'
              label='Empresa'
              required={true}
              options={fornecedoresDropDown}
              getOptionKey={x => x.idFornecedor}
              getOptionLabel={x => x.nomeFantasia}
              reactHookForm={hookForm}
            />
          }
          {
            (Number(realizadoPor) === 0 && idFornecedor !== null && idFornecedor !== undefined) &&
            <DefaultFormMultiAutocomplete
              name='funcionarios'
              label='Funcionários'
              required={Number(realizadoPor) === 1}
              options={funcionariosTercDropDown.filter(x => x.idFornecedor === idFornecedor)}
              getOptionKey={x => x.idFuncionarioTerc}
              getOptionLabel={x => x.nome ?? ''}
              reactHookForm={hookForm}
            />
          }
          {
            Number(realizadoPor) === 1 &&
            <DefaultFormMultiAutocomplete
              name='funcionarios'
              label='Funcionários'
              required={Number(realizadoPor) === 1}
              options={funcionariosDropDown}
              getOptionDisabled={x => !x.canBeSelected}
              getOptionKey={x => x.idFuncionario}
              getOptionLabel={x => x.nome}
              reactHookForm={hookForm}
            />
          }
        </>
      </FormContainer>
    </BaseFormControlledModal>
  );
}